import {
  Col,
  DatePicker,
  Form,
  message,
  Modal,
  Row,
  Select,
  Spin,
  Table,
} from "antd";
import COLORS from "common/Colors";
import {
  APPROVAL_FILTER_TYPES,
  MONTH_LIST,
  NON_EMPTY_REGEX,
  STATUS_CODE,
} from "common/Constants";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  addMonthinDate,
  dateInDetail,
  getinitialdate,
  getKenyanDateTime,
  getMonthDateRange,
  getMySqlDate,
  momentDate,
} from "utils/Date";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { CALL_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";
import { EditOutlined, DeleteOutlined, CloseOutlined } from "@ant-design/icons";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";
import TextArea from "antd/lib/input/TextArea";
import { find, get } from "lodash";
import download from "downloadjs";
import ViewDocument from "reusableComponent/Popup/viewDocument";
import ViewMore from "organisms/ViewMore";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";

dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.extend(advancedFormat);

const ModalWrap = styled(Modal)`
  .ant-modal-content {
    border-radius: 14px;
    padding: 10px 8px 0px 8px;
  }

  .ant-modal-header {
    border-bottom: none;
    padding-bottom: 0;
  }

  .ant-modal-title {
    font-size: 20px;
    font-weight: 600;
    margin-top: -10px;
  }

  .ant-modal-body {
    padding: 10px 20px 24px;
  }

  /* top right icons */
  .top-icons {
    position: absolute;
    right: 55px;
    top: 18px;
    display: flex;
    gap: 18px;
    font-size: 18px;
    color: #5b6fd6;
    cursor: pointer;
  }

  .close-icon {
    position: absolute;
    right: 20px;
    top: 18px;
    font-size: 18px;
    cursor: pointer;
    color: #666;
  }

  .applied-tag {
    background: #e9ecfb;
    border-radius: 20px;
    padding: 3px 14px;
    font-size: 13px;
    margin-bottom: 18px;
    display: inline-block;
  }

  .label {
    color: #8b8b8b;
    font-weight: 500;
  }

  .value {
    color: #1f1f1f;
    font-weight: 500;
    // text-align: right;
  }

  .attachment {
    color: #2d53da;
    cursor: pointer;
  }

  .reason-title {
    margin-top: 16px;
    color: #8b8b8b;
    font-weight: 500;
  }

  .reason-text {
    margin-top: 6px;
    color: #1f1f1f;
    line-height: 1.6;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .value {
      text-align: left;
      margin-top: 4px;
    }

    .top-icons {
      right: 45px;
      gap: 12px;
    }

    .ant-modal-body {
      padding: 10px 14px 22px;
    }
  }
`;

const OvertimeApprovalWrap = styled.div`
  .ant-input::placeholder,
  .ant-picker-input > input::placeholder {
    color: #9ca3af !important;
  }

  .ant-select-selection-placeholder {
    color: #9ca3af !important;
  }

  .ant-input,
  .ant-picker,
  .ant-select-selector {
    border-radius: 24px !important;
  }

  .ant-btn {
    width: 100%;
  }
`;

const StyledTable = styled(Table)`
  .ant-table {
    border: 1px solid #ccc;
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-table-container {
    border-radius: 12px;
  }

  .ant-table-content {
    overflow-x: auto;
  }

  table {
    width: 100%;
    min-width: 900px;
  }

  .ant-table-cell {
    padding: 16px;
    white-space: wrap;
    overflow: hidden;
  }

  .ant-table-thead .ant-table-cell {
    font-weight: 600;
    color: ${COLORS.SECONDARY_BLACK};
  }

  .reason-column {
    min-width: 280px;
    white-space: normal;
  }

  .non-white {
    background: #f9f9f9;
  }

  @media (max-width: 768px) {
    .ant-table-cell {
      padding: 10px;
      font-size: 12px;
    }
  }
`;

const OvertimeApproval = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [updateform] = Form.useForm();
  const containerRef = useRef(null);
  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);
  const durationType = Form.useWatch("duration_type", form);
  const statusType = Form.useWatch("status", form);
  const pageTitleRef = useRef(null);
  const [data, setData] = useState([]);
  const [viewData, setViewData] = useState({});
  const [open, setOpen] = useState(false);
  const [scrollY, setScrollY] = useState(120);
  const [loading, setLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [isOpenModal, setIsModalOpen] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [previewURL, setPreviewURL] = useState();
  const [CSVDataList, setCSVDataList] = useState([]);
  const csvLinkEl = useRef();
  const higherOfficialsEmailId = userInfo?.higherOfficialsList?.find(
    (item) => item?.role === "CEO"
  )?.email;

  const isHigherOfficials = higherOfficialsEmailId === userInfo?.userid;

  const prepareCSV = async (event, done) => {
    if (data?.length > 0) {
      const csvData = data.map((item) => {
        const user = userList?.find((val) => val?.userid === item?.userId);

        const baseObj = {
          Name: user?.fullname || "-",
          "Employee ID": user?.employeeid || "-",
          "Date - Day": item?.overtimeDate
            ? dayjs(item.overtimeDate).format("Do MMM YYYY - ddd")
            : "-",
          Time: item?.overtimeTime
            ? dayjs(item.overtimeTime).format("HH [hrs] mm [mins]")
            : "-",
          "Applied on": item?.createdAt
            ? dayjs(item.createdAt).format("Do MMM YYYY - ddd")
            : "-",
          "Reason for Overtime": item?.overtimeReason || "-",
        };

        if (statusType === "0") {
          baseObj["Level 1 Approved by"] =
            item?.approved_rejected_byName || "-";
        }

        if (statusType === "1") {
          baseObj["Level 1 Approved by"] =
            item?.approved_rejected_byName || "-";
          baseObj["Level 2 Approved by"] =
            item?.approved_rejected_by_higherofficials || "-";
        }

        if (statusType === "2") {
          baseObj["Rejected by"] = item?.approved_rejected_byName || "-";
          baseObj["Decline Reason"] = item?.rejectReason || "-";
        }

        return baseObj;
      });

      setCSVDataList(csvData);

      setTimeout(() => {
        csvLinkEl.current.link.click();
      });
    } else {
      message.error(t("No data available to download."));
    }
  };

  function weeks(days, date) {
    var date = new Date(date);
    date.setDate(date.getDate() + days);
    return date.toISOString().slice(0, 10);
  }

  const getData = async () => {
    const { start_date, duration_type, month, end_date } =
      form.getFieldsValue();
    const { startDateOfMonth, lastDateOfMonth } = getMonthDateRange(
      dateInDetail(getKenyanDateTime()).year,
      get(
        find(MONTH_LIST, { value: month || dayjs().format("MMMM") }),
        "index",
        0
      )
    );
    const status = form.getFieldValue("status");

    const startDate = getMySqlDate(
      duration_type === 2
        ? startDateOfMonth
        : duration_type === 4
        ? momentDate(addMonthinDate(getinitialdate()))
        : dayjs(start_date).format("YYYY-MM-DD")
    );
    const endDate = getMySqlDate(
      duration_type === 2
        ? lastDateOfMonth
        : duration_type === 3
        ? weeks(7, getMySqlDate(start_date))
        : duration_type === 4
        ? momentDate(addMonthinDate(getKenyanDateTime(), 3))
        : dayjs(end_date).format("YYYY-MM-DD")
    );
    setLoading(true);
    const { code, data } = await CALL_API(
      `employeerequest/overtimerequest?reportingManager=${userInfo?.userid}&statusId=${status}&startDate=${startDate}&endDate=${endDate}`,
      "get"
    );

    if (code === STATUS_CODE.SUCCESS) {
      setData(data || []);
      setLoading(false);
    }
  };

  const handlePreviewClick = async (data) => {
    console.log("data12-->", data);
    try {
      const preview = data?.fileName?.split(".")?.pop();
      setLoading(true);

      const { code, url } = await CALL_API(
        `employeerequest/file?userId=${data?.userId}&requestId=${
          data?.requestId
        }&requestType=${"overtimerequest"}`,
        "get",
        {}
      );

      if (code === STATUS_CODE.SUCCESS) {
        if (["jpg", "jpeg", "png", "svg", "pdf"].includes(preview)) {
          setPreviewURL(url);
          setPreview(true);
        } else {
          download(url);
          setPreview(false);
        }
      } else {
        message.error(t("Preview failed. Try again."));
      }
    } catch (err) {
      console.log(err);
      message.error(t("Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async (status, value) => {
    console.log("values-->", value);
    try {
      setUpdateLoading(true);
      const resolvedStatus =
        status === "1" ? (isHigherOfficials ? "1" : "0") : status;
      const payload = {
        overtimeDate: dayjs(viewData.overtimeDate).format("YYYY-MM-DD"),
        overtimeTime: viewData?.overtimeTime,
        overtimeReason: viewData.overtimeReason,
        reportingManager: viewData?.reportingManager,
        objectKey: viewData?.objectKey || "",
        fileName: viewData?.fileName || "",
        statusId: resolvedStatus,
        rejectReason: value?.decline_reason || "",
        requestType: "overtimerequest",
        approved_rejected_byName:
          status === "1" && isHigherOfficials
            ? viewData?.approved_rejected_byName || userInfo?.name
            : userInfo?.name,
        approved_rejected_by_higherofficials:
          status === "1" && isHigherOfficials ? userInfo?.name : "",
      };

      const url = `employeerequest/${viewData?.userId}/${
        viewData?.requestId
      }?requestType=${"overtimerequest"}`;

      const method = "patch";

      const { code } = await CALL_API(url, method, payload);

      if (code === STATUS_CODE.SUCCESS) {
        setPopupOpen(true);
        setOpen(false);
        setViewData({});
        setIsModalOpen(false);
        getData();
        setTimeout(() => {
          setPopupOpen(false);
          updateform.resetFields();
        }, 2000);
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setUpdateLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [form, userInfo]);

  useEffect(() => {
    if (!pageTitleRef.current) return;

    const baseOffset = 300;
    const minHeight = 120;

    const calculateScrollY = () => {
      const headerHeight = pageTitleRef.current.offsetHeight;

      const availableHeight = window.innerHeight - headerHeight - baseOffset;

      setScrollY(Math.max(minHeight, availableHeight));
    };

    calculateScrollY();

    const resizeObserver = new ResizeObserver(() => {
      calculateScrollY();
    });

    resizeObserver.observe(pageTitleRef.current);
    window.addEventListener("resize", calculateScrollY);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", calculateScrollY);
    };
  }, []);

  const columns = [
    {
      title: t("Name"),
      dataIndex: "userId",
      key: "userId",
      width: 150,
      render: (text) => {
        return userList?.find((val) => val?.userid === text)?.fullname;
      },
    },
    {
      title: t("Employee ID"),
      dataIndex: "userId",
      key: "userId",
      width: 150,
      render: (text) => {
        return userList?.find((val) => val?.userid === text)?.employeeid;
      },
    },
    {
      title: t("Date - Day"),
      dataIndex: "overtimeDate",
      key: "overtimeDate",
      width: 150,
      render: (date) => {
        return dayjs(date)?.format("Do MMM YYYY - ddd");
      },
    },
    {
      title: t("Time"),
      dataIndex: "overtimeTime",
      key: "overtimeTime",
      width: 150,
      render: (date) => {
        return dayjs(date)?.format("HH [hrs] mm [mins]");
      },
    },

    {
      title: t("Attachment"),
      dataIndex: "fileName",
      key: "fileName",
      width: 150,
      render: (text, record) => (
        <a
          href="#"
          style={{ color: "#2d53da" }}
          className="text-wrap"
          onClick={(e) => {
            e.stopPropagation();
            handlePreviewClick(record);
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: t("Applied on"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date) => {
        return dayjs(date)?.format("Do MMM YYYY - ddd");
      },
    },
    {
      title: t("Reason for Overtime"),
      dataIndex: "overtimeReason",
      key: "reason",
      width: 150,
      className: "reason-column",
    },
  ];
  if (statusType === "0" && isHigherOfficials) {
    columns.push({
      title: t("Level 1 Approved by"),
      dataIndex: "approved_rejected_byName",
      key: "approved_rejected_byName",
      width: 150,
      render: (text) => (text ? text : "-"),
    });
  }

  if (statusType === "1") {
    columns.push(
      {
        title: t("Level 1 Approved by"),
        dataIndex: "approved_rejected_byName",
        key: "approved_rejected_byName",
        width: 150,
        render: (text) => {
          return text ? text : "-";
        },
      },
      {
        title: t("Level 2 Approved by"),
        dataIndex: "approved_rejected_by_higherofficials",
        key: "approved_rejected_by_higherofficials",
        width: 150,
        render: (text) => {
          return text ? text : "-";
        },
      }
    );
  }
  if (statusType === "2") {
    columns.push(
      {
        title: t("Rejected by"),
        dataIndex: "approved_rejected_byName",
        key: "approved_rejected_byName",
        width: 150,
        render: (text) => {
          return text ? text : "-";
        },
      },
      {
        title: t("Decline Reason"),
        dataIndex: "rejectReason",
        key: "rejectReason",
        width: 150,
        className: "normal-column",
        render: (reason) => {
          return (
            <>
              <ViewMore value={reason || "-"} />
            </>
          );
        },
      }
    );
  }

  const canApprove =
    (!isHigherOfficials && !viewData?.approved_rejected_byName) ||
    (isHigherOfficials && !!viewData?.approved_rejected_byName);

  const validateDateRange = () => {
    const endDate = dayjs(form.getFieldValue("end_date"));
    const startDate = dayjs(form.getFieldValue("start_date"));
    if (dayjs(startDate).isAfter(endDate)) {
      form.setFieldsValue({
        end_date: startDate,
      });
      getData();
    }
  };

  const validateEndDateRange = () => {
    const startDate = dayjs(form.getFieldValue("start_date"));
    const endDate = dayjs(form.getFieldValue("end_date"));
    if (dayjs(endDate).isBefore(startDate)) {
      form.setFieldsValue({
        start_date: endDate,
      });
      getData();
    }
  };

  return (
    <OvertimeApprovalWrap>
      <div className="w-100 d-flex justify-content-between align-items-center">
        <>
          <h6>{t("Overtime Approval")}</h6>
        </>
        <div>
          <ButtonOutlined
            style={{
              borderRadius: "10px",
            }}
            onClick={prepareCSV}
            disabled={data.length === 0}
          >
            {t("Export CSV file")}
          </ButtonOutlined>
          <CSVLink
            data={CSVDataList}
            filename={"overtime.csv"}
            asyncOnClick={true}
            ref={csvLinkEl}
          />
        </div>
      </div>
      <div className="page-title mt-3" ref={pageTitleRef}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: "0",
            duration_type: 4,
            month: dateInDetail(getKenyanDateTime()).monthName,
            status_id: "4",
            start_date: dayjs(),
            end_date: dayjs(),
          }}
          onValuesChange={getData}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8} lg={4} ref={containerRef}>
              <Form.Item name="status">
                <Select
                  placeholder={t("Status")}
                  options={[
                    { label: t("Waiting For Approval"), value: "0" },
                    { label: t("Approved"), value: "1" },
                    { label: t("Rejected"), value: "2" },
                  ]}
                  getPopupContainer={() => containerRef.current}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={4} ref={containerRef}>
              <Form.Item name="duration_type">
                <Select
                  placeholder={t("Select...")}
                  options={APPROVAL_FILTER_TYPES.map((item) => ({
                    ...item,
                    label: t(item.label),
                  }))}
                  getPopupContainer={() => containerRef.current}
                />
              </Form.Item>
            </Col>

            {durationType === 1 && (
              <>
                <Col xs={24} sm={12} md={8} lg={4} ref={containerRef}>
                  <Form.Item name="start_date">
                    <DatePicker
                      placeholder={t("Start date")}
                      format="DD-MM-YYYY"
                      className="w-100"
                      allowClear
                      onChange={validateDateRange}
                      getPopupContainer={() => containerRef.current}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8} lg={4} ref={containerRef}>
                  <Form.Item name="end_date">
                    <DatePicker
                      className="w-100"
                      placeholder={t("End date")}
                      format="DD-MM-YYYY"
                      allowClear
                      onChange={validateEndDateRange}
                      getPopupContainer={() => containerRef.current}
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {durationType === 2 && (
              <Col xs={24} sm={12} md={8} lg={4} ref={containerRef}>
                <Form.Item name="month">
                  <Select
                    placeholder="Select..."
                    options={MONTH_LIST}
                    getPopupContainer={() => containerRef.current}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </div>
      <Spin spinning={loading}>
        <StyledTable
          columns={columns}
          dataSource={data}
          pagination={false}
          scroll={{ y: scrollY }}
          style={{ minHeight: scrollY + 50 }}
          rowClassName={(_, index) => (index % 2 ? "non-white" : "")}
          onRow={(record) => {
            return {
              onClick: () => {
                setOpen(true);
                setViewData(record);
              },
            };
          }}
        />
      </Spin>
      <ModalWrap
        open={open}
        onCancel={() => {
          setOpen(false);
          setViewData({});
        }}
        footer={null}
        centered
        width={520}
        closable={false}
        title={t("Overtime Request")}
      >
        {/* top icons */}
        {/* <div className="top-icons">
          <EditOutlined />
          <DeleteOutlined />
        </div> */}

        <CloseOutlined
          className="close-icon"
          onClick={() => {
            setOpen(false);
            setViewData({});
          }}
        />

        {/* applied tag */}
        <div className="applied-tag">
          {t("Applied on")}&nbsp;&nbsp;
          <b className="value">
            {dayjs(viewData?.createdAt).format("Do MMM, YYYY")}
          </b>
        </div>

        {/* start date */}
        <Row gutter={[8, 14]}>
          <Col xs={24} sm={12}>
            <div className="label">{t("Date")}</div>
          </Col>
          <Col xs={24} sm={12}>
            <div className="value">
              {dayjs(viewData?.overtimeDate)?.format("Do MMM YYYY - ddd")}
            </div>
          </Col>

          {/* end date */}
          <Col xs={24} sm={12}>
            <div className="label">{t("Time")}</div>
          </Col>
          <Col xs={24} sm={12}>
            <div className="value">
              {dayjs(viewData?.overtimeTime)?.format("HH [hrs] mm [mins]")}
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div className="label">{t("Attachment")}</div>
          </Col>
          <Col xs={24} sm={12}>
            <div
              className="value attachment"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setOpen(false);
                handlePreviewClick(viewData);
              }}
            >
              {viewData?.fileName ? viewData?.fileName : "-"}
            </div>
          </Col>
          {statusType === "0" && viewData?.approved_rejected_byName && (
            <>
              <Col xs={24} sm={12}>
                <div className="label">{t("Level 1 Approved by")}</div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="value">
                  {viewData?.approved_rejected_byName}
                </div>
              </Col>
            </>
          )}
        </Row>

        {/* reason */}
        <div className="reason-title">{t("Reason for Overtime")}</div>
        <div className="reason-text">{viewData?.overtimeReason}</div>
        {statusType === "0" && (
          <div className="w-100 text-end mt-3">
            <ButtonOutlined
              onClick={() => {
                setOpen(false);
                setIsModalOpen(true);
              }}
            >
              {t("Decline")}
            </ButtonOutlined>
            {canApprove ? (
              <ButtonFilled
                onClick={() => {
                  handleFinish("1");
                }}
                loading={updateLoading}
              >
                {t("Approve")}
              </ButtonFilled>
            ) : (
              <span style={{ marginLeft: 10 }}>
                {isHigherOfficials
                  ? t("Pending by level 1 approval")
                  : t("Pending by level 2 approval")}
              </span>
            )}
          </div>
        )}
      </ModalWrap>
      <ModalWrap
        title=""
        visible={isOpenModal}
        centered
        keyboard={false}
        maskClosable={false}
        width={400}
        footer={[]}
        onCancel={() => setIsModalOpen(false)}
      >
        <Form
          form={updateform}
          name="reasonform"
          layout="vertical"
          initialValues={{
            reason: "",
          }}
          onFinish={(values) => handleFinish("2", values)}
        >
          <Form.Item
            label={t("Reason for Rejection")}
            name="decline_reason"
            rules={[
              {
                required: true,
                pattern: NON_EMPTY_REGEX,
                message: t("Please enter reject reason!"),
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder={t("Type something..")}
              maxLength={500}
            />
          </Form.Item>
          <div className="align-right">
            <ButtonOutlined
              onClick={() => {
                setIsModalOpen(false);
              }}
            >
              {t("Cancel")}
            </ButtonOutlined>

            <ButtonFilled
              htmlType="submit"
              type="primary"
              loading={updateLoading}
            >
              {t("Decline")}
            </ButtonFilled>
          </div>
        </Form>
      </ModalWrap>
      <SuccessDeclinePopup
        open={popupOpen}
        setOpen={setPopupOpen}
        type={
          updateform?.getFieldValue("decline_reason") ? "decline" : "success"
        }
        message={
          updateform?.getFieldValue("decline_reason")
            ? t("Declined")
            : t("Approved")
        }
      />
      <ViewDocument
        open={preview}
        onCancel={() => {
          setPreview(false);
          setPreviewURL("");
        }}
        data={previewURL}
      />
    </OvertimeApprovalWrap>
  );
};

export default OvertimeApproval;
